import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { v4 as uuidv4 } from 'uuid';
import type {
  ReceiptTemplate,
  ReceiptSection,
  ReceiptSettings,
  SectionType,
  SectionConfig,
} from '@/lib/templates/schema';
import { createSection, createDefaultTemplate } from '@/lib/templates/defaults';

interface ReceiptBuilderState {
  template: ReceiptTemplate;
  baseTemplate: ReceiptTemplate;
  selectedSectionId: string | null;
  isDirty: boolean;
  // Set when the current template was loaded from the user's saved list.
  // Allows the Save button to update the existing row instead of creating a new one.
  savedTemplateId: string | null;
  // Brand key whose preset a brand page last auto-applied. Persisted so a
  // login redirect (or any reload) remounting the same brand page does not
  // re-apply the preset over the user's in-progress work.
  lastAppliedBrand: string | null;

  // Template actions
  setTemplate: (template: ReceiptTemplate, savedTemplateId?: string | null) => void;
  setLastAppliedBrand: (brandKey: string) => void;
  updateSettings: (settings: Partial<ReceiptSettings>) => void;
  resetTemplate: () => void;
  markSaved: (savedTemplateId: string) => void;

  // Section actions
  addSection: (type: SectionType, index?: number) => void;
  removeSection: (id: string) => void;
  updateSection: (id: string, config: Partial<SectionConfig>) => void;
  toggleSection: (id: string) => void;
  moveSection: (fromIndex: number, toIndex: number) => void;
  duplicateSection: (id: string) => void;

  // Selection
  selectSection: (id: string | null) => void;

  // Get selected section
  getSelectedSection: () => ReceiptSection | null;
}

export const useReceiptBuilder = create<ReceiptBuilderState>()(persist((set, get) => ({
  template: createDefaultTemplate(),
  baseTemplate: createDefaultTemplate(),
  selectedSectionId: null,
  isDirty: false,
  savedTemplateId: null,
  lastAppliedBrand: null,

  setLastAppliedBrand: (brandKey) => set({ lastAppliedBrand: brandKey }),

  setTemplate: (template, savedTemplateId = null) => {
    const snapshot = JSON.parse(JSON.stringify(template));
    const firstId = template.sections.length > 0 ? template.sections[0].id : null;
    set({
      template,
      baseTemplate: snapshot,
      isDirty: false,
      selectedSectionId: firstId,
      savedTemplateId,
    });
  },

  markSaved: (savedTemplateId) => {
    set((state) => ({
      savedTemplateId,
      baseTemplate: JSON.parse(JSON.stringify(state.template)),
      isDirty: false,
    }));
  },

  updateSettings: (settings) => {
    set((state) => ({
      template: {
        ...state.template,
        settings: { ...state.template.settings, ...settings },
        updatedAt: new Date().toISOString(),
      },
      isDirty: true,
    }));
  },

  resetTemplate: () => {
    const base = get().baseTemplate;
    const restored = JSON.parse(JSON.stringify(base));
    const firstId = restored.sections.length > 0 ? restored.sections[0].id : null;
    set({
      template: restored,
      selectedSectionId: firstId,
      isDirty: false,
    });
  },

  addSection: (type, index) => {
    const newSection = createSection(type);
    set((state) => {
      const sections = [...state.template.sections];
      if (index !== undefined) {
        sections.splice(index, 0, newSection);
      } else {
        sections.push(newSection);
      }
      return {
        template: {
          ...state.template,
          sections,
          updatedAt: new Date().toISOString(),
        },
        selectedSectionId: newSection.id,
        isDirty: true,
      };
    });
  },

  removeSection: (id) => {
    set((state) => ({
      template: {
        ...state.template,
        sections: state.template.sections.filter((s) => s.id !== id),
        updatedAt: new Date().toISOString(),
      },
      selectedSectionId:
        state.selectedSectionId === id ? null : state.selectedSectionId,
      isDirty: true,
    }));
  },

  updateSection: (id, config) => {
    set((state) => {
      const newSections = state.template.sections.map((section) => {
        if (section.id !== id) return section;
        // Use type assertion to handle the union type
        return {
          ...section,
          config: { ...section.config, ...config },
        } as ReceiptSection;
      });
      
      return {
        template: {
          ...state.template,
          sections: newSections,
          updatedAt: new Date().toISOString(),
        },
        isDirty: true,
      };
    });
  },

  toggleSection: (id) => {
    set((state) => ({
      template: {
        ...state.template,
        sections: state.template.sections.map((section) =>
          section.id === id ? { ...section, enabled: !section.enabled } : section
        ),
        updatedAt: new Date().toISOString(),
      },
      isDirty: true,
    }));
  },

  moveSection: (fromIndex, toIndex) => {
    set((state) => {
      const sections = [...state.template.sections];
      const [movedSection] = sections.splice(fromIndex, 1);
      sections.splice(toIndex, 0, movedSection);
      return {
        template: {
          ...state.template,
          sections,
          updatedAt: new Date().toISOString(),
        },
        isDirty: true,
      };
    });
  },

  duplicateSection: (id) => {
    set((state) => {
      const sectionIndex = state.template.sections.findIndex((s) => s.id === id);
      if (sectionIndex === -1) return state;

      const originalSection = state.template.sections[sectionIndex];
      const duplicatedSection = {
        ...JSON.parse(JSON.stringify(originalSection)),
        id: uuidv4(),
      };

      const sections = [...state.template.sections];
      sections.splice(sectionIndex + 1, 0, duplicatedSection);

      return {
        template: {
          ...state.template,
          sections,
          updatedAt: new Date().toISOString(),
        },
        selectedSectionId: duplicatedSection.id,
        isDirty: true,
      };
    });
  },

  selectSection: (id) => {
    set({ selectedSectionId: id });
  },

  getSelectedSection: () => {
    const state = get();
    if (!state.selectedSectionId) return null;
    return (
      state.template.sections.find((s) => s.id === state.selectedSectionId) ||
      null
    );
  },
}), {
  name: 'receipt-builder',
  partialize: (state) => ({
    template: state.template,
    baseTemplate: state.baseTemplate,
    savedTemplateId: state.savedTemplateId,
    lastAppliedBrand: state.lastAppliedBrand,
  }),
}));

// Expose store to window for debugging (dev only)
if (typeof window !== 'undefined') {
  (window as unknown as { useReceiptBuilder: typeof useReceiptBuilder }).useReceiptBuilder = useReceiptBuilder;
}

